import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

// URL-addressable guest mode: `?guest=1` puts the app into read-only guest mode on
// load, derived from the URL every time rather than from a cookie or browser storage.
// This is what lets the demo work embedded in a third-party iframe, where the session
// cookie is partitioned/blocked. The matching backend allows anonymous reads in
// DEMO_MODE, so no server session is needed for a guest to view the demo project.
const isUrlGuest = () => {
  try {
    return new URLSearchParams(window.location.search).get('guest') === '1';
  } catch {
    return false;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check authentication status on mount.
  useEffect(() => {
    // A URL guest is authenticated purely from the URL — no cookie round-trip. This
    // must not depend on (or wait for) checkAuth, so it survives cross-origin iframes
    // and cleared browser storage. Real users (no ?guest=1) take the normal path.
    if (isUrlGuest()) {
      setUser({ username: 'guest_url', is_guest: true });
      setIsAuthenticated(true);
      setLoading(false);
      return;
    }
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get('/api/auth/check/', {
        withCredentials: true
      });
      
      if (response.data.authenticated) {
        setUser(response.data.user);
        setIsAuthenticated(true);
      } else {
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      const response = await axios.post('/api/auth/login/', {
        username,
        password
      }, {
        withCredentials: true
      });

      if (response.data.success) {
        setUser(response.data.user);
        setIsAuthenticated(true);
        return { success: true };
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Login failed';
      return { success: false, error: errorMessage };
    }
  };

  const demoLogin = async () => {
    try {
      await axios.get(`/api/csrf/`, { withCredentials: true });
      const response = await axios.post(`/api/auth/demo-login/`, {}, {
        withCredentials: true
      });
      if (response.data.success) {
        setUser({ ...response.data.user, is_guest: true });
        setIsAuthenticated(true);
        return { success: true };
      }
    } catch (error) {
      return { success: false, error: `Demo login failed` };
    }
  };

  const logout = async () => {
    try {
      await axios.post('/api/auth/logout/', {}, {
        withCredentials: true
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, loading, login, logout, checkAuth, demoLogin }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};



